import { isAxiosError } from "axios";
import { PlayCircle } from "lucide-react";
import type { ChangeEvent } from "react";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import {
	type PipelineAdminRunResponse,
	type PipelineDispatchResponse,
	type PipelineIntentResponse,
	runPipelineAdminRun,
	runPipelineDispatch,
	runPipelineIntent,
	runPipelineKnowledgeSandbox,
	runPipelineOAuthHealth,
	runPipelineSkillSandbox,
} from "@/lib/api/pipeline";
import {
	eventLabel,
	finalActionLabel,
	intentLabel,
	intentRuleLabel,
	knowledgeSourceLabel,
	matchModeLabel,
	playbookLabel,
	qualityDecisionLabel,
	sampleLabel,
	serviceStageLabel,
	skillLabel,
	skillListLabel,
} from "@/lib/product-labels";
import {
	SINGLE_STAGE_TARGET_STAGE_LABELS,
	type SingleStageRuntimeKind,
	type SingleStageRuntimeResultSnapshot,
	type SingleStageRuntimeTargetStage,
	createSingleStageRunRecord,
	saveSingleStageRunRecord,
} from "@/lib/single-stage-run-records";

export type { SingleStageRuntimeKind, SingleStageRuntimeTargetStage };

type SingleStageRuntimeStatus = "idle" | "running" | "success" | "error";

type SingleStageRuntimeResult = SingleStageRuntimeResultSnapshot;

type RuntimeHistoryMessage = { role: "user" | "agent"; content: string };

type SingleStageRuntimeContext = {
	history: RuntimeHistoryMessage[];
	previousIntent: string | null;
	currentPlaybookId: string | null;
};

type SingleStageRuntimePanelProps = {
	kind: SingleStageRuntimeKind;
	targetStage: SingleStageRuntimeTargetStage;
	sampleId: string;
	sampleMessage: string;
	expectedIntent: string;
	expectedPlaybook: string;
	assetName: string;
	boundary: string;
};

const MERCHANT_ID = "mer_nY0bZmLI";
const CUSTOM_INPUT_SAMPLE_ID = "custom_input";
const CUSTOM_INPUT_ASSET_NAME = "自定义问题运行";
const CUSTOM_INPUT_BOUNDARY =
	"本次只展示实际返回，不复用代表样本预期；需要闭环时先固化为评测样本。";

const KIND_COPY: Record<
	SingleStageRuntimeKind,
	{
		title: string;
		button: string;
		running: string;
		idle: string;
	}
> = {
	intent: {
		title: "分类测试运行",
		button: "运行意图分类",
		running: "意图分类运行中...",
		idle: "输入或保留代表问题，点击后只验证客户意图是否识别正确。",
	},
	playbook: {
		title: "方案测试运行",
		button: "运行处理方案",
		running: "处理方案运行中...",
		idle: "输入或保留代表问题，点击后只验证方案是否被正确召回。",
	},
	tools: {
		title: "兼容样本回放",
		button: "运行兼容样本",
		running: "样本回放运行中...",
		idle: "输入或保留代表问题，点击后检查 OAuth、绑定工具沙盒和知识辅助；Skill 是否应执行以上方 Skill 判断测试为准。",
	},
	knowledge: {
		title: "检索验证运行",
		button: "运行知识检索",
		running: "知识检索运行中...",
		idle: "输入或保留代表问题，点击后只验证知识片段能否被找回。",
	},
	prompt: {
		title: "回复生成运行",
		button: "运行回复生成",
		running: "回复生成运行中...",
		idle: "输入或保留代表问题，点击后看主 Agent 如何消费证据生成回复。",
	},
	quality: {
		title: "质量策略运行",
		button: "运行质量策略",
		running: "质量策略运行中...",
		idle: "输入或保留代表问题，点击后看回复是否应放行、改写、阻断或转人工。",
	},
};

export function SingleStageRuntimePanel({
	kind,
	targetStage,
	sampleId,
	sampleMessage,
	expectedIntent,
	expectedPlaybook,
	assetName,
	boundary,
}: SingleStageRuntimePanelProps) {
	const copy = KIND_COPY[kind];
	const [message, setMessage] = useState(sampleMessage);
	const [status, setStatus] = useState<SingleStageRuntimeStatus>("idle");
	const [result, setResult] = useState<SingleStageRuntimeResult | null>(null);
	const [error, setError] = useState<string | null>(null);
	const runtimeContext = useMemo(() => initialRuntimeContext(), []);
	const isCustomInput = isCustomRuntimeMessage(message, sampleMessage);

	useEffect(() => {
		setMessage(sampleMessage);
		setResult(null);
		setError(null);
		setStatus("idle");
	}, [sampleMessage]);

	const evalHref = useMemo(
		() =>
			createEvalHref({
				targetStage,
				sampleId,
			}),
		[targetStage, sampleId],
	);

	function handleMessageChange(event: ChangeEvent<HTMLTextAreaElement>) {
		setMessage(event.target.value);
		setResult(null);
		setError(null);
		setStatus("idle");
	}

	async function run() {
		const trimmed = message.trim();
		if (!trimmed) return;
		const isCustomRun = isCustomRuntimeMessage(trimmed, sampleMessage);
		setStatus("running");
		setError(null);
		try {
			const runtimeResult = await runRuntime(kind, trimmed, runtimeContext);
			const nextResult = isCustomRun
				? withCustomInputScope(runtimeResult)
				: withExpectedComparison(runtimeResult, kind, expectedIntent, expectedPlaybook);
			setResult(nextResult);
			saveSingleStageRunRecord(
				createSingleStageRunRecord({
					runKind: kind,
					targetStage,
					sampleId: isCustomRun ? CUSTOM_INPUT_SAMPLE_ID : sampleId,
					message: trimmed,
					expectedIntent: isCustomRun ? "" : expectedIntent,
					expectedPlaybook: isCustomRun ? "" : expectedPlaybook,
					assetName: isCustomRun ? CUSTOM_INPUT_ASSET_NAME : assetName,
					boundary: isCustomRun ? CUSTOM_INPUT_BOUNDARY : boundary,
					merchantId: MERCHANT_ID,
					result: nextResult,
				}),
			);
			setStatus("success");
		} catch (err) {
			const nextError = errorMessage(err);
			const errorResult: SingleStageRuntimeResult = {
				title: `${SINGLE_STAGE_TARGET_STAGE_LABELS[targetStage]}调用失败`,
				status: "error",
				summary: [{ label: "错误信息", value: nextError }],
				raw: { error: nextError },
			};
			setResult(errorResult);
			setError(nextError);
			saveSingleStageRunRecord(
				createSingleStageRunRecord({
					runKind: kind,
					targetStage,
					sampleId: isCustomRun ? CUSTOM_INPUT_SAMPLE_ID : sampleId,
					message: trimmed,
					expectedIntent: isCustomRun ? "" : expectedIntent,
					expectedPlaybook: isCustomRun ? "" : expectedPlaybook,
					assetName: isCustomRun ? CUSTOM_INPUT_ASSET_NAME : assetName,
					boundary: isCustomRun ? CUSTOM_INPUT_BOUNDARY : boundary,
					merchantId: MERCHANT_ID,
					result: errorResult,
				}),
			);
			setStatus("error");
		}
	}

	return (
		<section
			className="rounded-md border border-surface-border bg-surface-card p-4"
			data-testid={`single-stage-runtime-panel-${kind}`}
		>
			<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
				<div className="space-y-1">
					<h3 className="text-base font-semibold text-text-primary">{copy.title}</h3>
					<p className="max-w-3xl text-sm leading-6 text-text-secondary">
						这里直接验证本页能力；端到端效果仍回到链路测试台和评测运行。
					</p>
				</div>
				{isCustomInput ? (
					<span className="inline-flex w-fit items-center rounded-md border border-brand-warning/30 bg-brand-warning/10 px-3 py-2 text-sm font-medium text-brand-warning">
						自定义问题需先固化为样本
					</span>
				) : (
					<a
						href={evalHref}
						className="inline-flex w-fit items-center rounded-md border border-surface-border bg-surface-background px-3 py-2 text-sm font-medium text-text-primary hover:bg-surface-subtle"
					>
						带入评测运营人工候选
					</a>
				)}
			</div>

			<div className="mt-4 grid gap-4 lg:grid-cols-[minmax(320px,0.8fr)_minmax(0,1.2fr)]">
				<div className="space-y-3">
					<label className="grid gap-1 text-sm">
						<span className="text-xs font-medium text-text-muted">模拟客户问题</span>
						<textarea
							value={message}
							onChange={handleMessageChange}
							className="min-h-24 rounded-md border border-surface-border bg-surface-background p-3 text-sm leading-6"
							data-testid={`single-stage-runtime-message-${kind}`}
						/>
					</label>
					<div className="grid gap-2 text-sm sm:grid-cols-2">
						{isCustomInput ? (
							<>
								<RuntimeFact label="输入类型" value="自定义问题" />
								<RuntimeFact label="预期口径" value="不套用样本预期" />
							</>
						) : (
							<>
								<RuntimeFact label="当前样本" value={sampleLabel(sampleId)} />
								<RuntimeFact label="预期分类" value={intentLabel(expectedIntent)} />
								<RuntimeFact label="预期方案" value={playbookLabel(expectedPlaybook)} />
							</>
						)}
						<RuntimeFact label="验证环节" value={SINGLE_STAGE_TARGET_STAGE_LABELS[targetStage]} />
						<RuntimeFact label="带入上文" value={`${runtimeContext.history.length} 条`} />
						<RuntimeFact label="上一轮分类" value={intentLabel(runtimeContext.previousIntent)} />
					</div>
					<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm leading-6 text-text-secondary">
						<div className="font-medium text-text-primary">
							{isCustomInput ? CUSTOM_INPUT_ASSET_NAME : assetName}
						</div>
						<p className="mt-1">{isCustomInput ? CUSTOM_INPUT_BOUNDARY : boundary}</p>
					</div>
					<Button
						type="button"
						onClick={run}
						disabled={status === "running" || !message.trim()}
						data-testid={`single-stage-runtime-run-${kind}`}
					>
						<PlayCircle className="mr-2 h-4 w-4" aria-hidden="true" />
						{status === "running" ? copy.running : copy.button}
					</Button>
				</div>

				<div className="rounded-md border border-surface-border bg-surface-background p-3">
					<div className="flex items-center justify-between gap-3">
						<div className="text-sm font-semibold text-text-primary">本次运行结果</div>
						<ResultPill status={result?.status ?? status} />
					</div>
					{error ? <div className="mt-3 text-sm leading-6 text-brand-danger">{error}</div> : null}
					{result ? (
						<div className="mt-3 space-y-3">
							<div className="grid gap-2 sm:grid-cols-2">
								{result.summary.map((item) => (
									<RuntimeFact key={item.label} label={item.label} value={item.value} />
								))}
							</div>
							<details className="rounded-md border border-surface-border bg-surface-subtle p-3">
								<summary className="cursor-pointer text-sm font-medium text-text-primary">
									查看原始返回
								</summary>
								<pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap break-words text-xs text-text-secondary">
									{JSON.stringify(result.raw, null, 2)}
								</pre>
							</details>
						</div>
					) : (
						<div className="mt-3 rounded-md border border-dashed border-surface-border bg-surface-subtle p-3 text-sm leading-6 text-text-secondary">
							{copy.idle}
						</div>
					)}
				</div>
			</div>
		</section>
	);
}

function RuntimeFact({ label, value }: { label: string; value: string }) {
	return (
		<div className="rounded-md border border-surface-border bg-surface-card px-3 py-2">
			<div className="text-xs font-medium text-text-muted">{label}</div>
			<div className="mt-1 break-words text-sm font-semibold leading-5 text-text-primary">
				{value}
			</div>
		</div>
	);
}

function ResultPill({
	status,
}: { status: SingleStageRuntimeStatus | SingleStageRuntimeResult["status"] }) {
	const copy =
		status === "running"
			? "运行中"
			: status === "success"
				? "已返回"
				: status === "partial"
					? "需复核"
					: status === "error"
						? "调用失败"
						: "未运行";
	const tone =
		status === "success"
			? "border-brand-success/30 bg-brand-success/10 text-brand-success"
			: status === "partial" || status === "running"
				? "border-brand-warning/30 bg-brand-warning/10 text-brand-warning"
				: status === "error"
					? "border-brand-danger/30 bg-brand-danger/10 text-brand-danger"
					: "border-surface-border bg-surface-subtle text-text-secondary";
	return (
		<span className={`rounded-full border px-2 py-1 text-xs font-medium ${tone}`}>{copy}</span>
	);
}

function initialRuntimeContext(): SingleStageRuntimeContext {
	if (typeof window === "undefined") {
		return { history: [], previousIntent: null, currentPlaybookId: null };
	}
	const params = new URLSearchParams(window.location.search);
	return {
		history: parseRuntimeHistory(params.get("history")),
		previousIntent:
			nonEmptyParam(params, "previous_intent") ??
			nonEmptyParam(params, "actual_intent") ??
			nonEmptyParam(params, "expected_intent"),
		currentPlaybookId:
			nonEmptyParam(params, "current_playbook_id") ??
			nonEmptyParam(params, "actual_pb") ??
			nonEmptyParam(params, "expected_pb"),
	};
}

function parseRuntimeHistory(raw: string | null): RuntimeHistoryMessage[] {
	if (!raw) return [];
	try {
		const parsed = JSON.parse(raw) as unknown;
		if (!Array.isArray(parsed)) return [];
		return parsed
			.map((item) => normalizeRuntimeHistoryMessage(item))
			.filter((item): item is RuntimeHistoryMessage => item !== null);
	} catch {
		return [];
	}
}

function normalizeRuntimeHistoryMessage(item: unknown): RuntimeHistoryMessage | null {
	if (!item || typeof item !== "object") return null;
	const record = item as Record<string, unknown>;
	const role =
		record.role === "assistant"
			? "agent"
			: record.role === "agent" || record.role === "user"
				? record.role
				: null;
	const content = typeof record.content === "string" ? record.content.trim() : "";
	if (!role || !content) return null;
	return { role, content };
}

function nonEmptyParam(params: URLSearchParams, key: string): string | null {
	const value = params.get(key)?.trim();
	return value ? value : null;
}

function contextualSandboxMessage(message: string, history: RuntimeHistoryMessage[]): string {
	if (history.length === 0) return message;
	const recent = history
		.slice(-2)
		.map((item) => `${item.role === "agent" ? "助手" : "用户"}：${item.content}`)
		.join("；");
	return `${message}（上文：${recent}）`;
}

async function runRuntime(
	kind: SingleStageRuntimeKind,
	message: string,
	context: SingleStageRuntimeContext,
): Promise<SingleStageRuntimeResult> {
	if (kind === "intent") {
		return withRuntimeContext(
			stageFromIntent(
				await runPipelineIntent({
					message,
					history: context.history,
					previous_intent: context.previousIntent,
				}),
			),
			context,
		);
	}
	if (kind === "playbook") {
		return withRuntimeContext(
			stageFromDispatch(
				await runPipelineDispatch({
					merchant_id: MERCHANT_ID,
					message,
					history: context.history,
					previous_intent: context.previousIntent,
					current_playbook_id: context.currentPlaybookId,
				}),
			),
			context,
		);
	}
	const contextualMessage = contextualSandboxMessage(message, context.history);
	if (kind === "tools") {
		const [oauth, skill, knowledge] = await Promise.allSettled([
			runPipelineOAuthHealth(MERCHANT_ID),
			runPipelineSkillSandbox(MERCHANT_ID, contextualMessage),
			runPipelineKnowledgeSandbox(MERCHANT_ID, contextualMessage),
		]);
		return stageFromTools(oauth, skill, knowledge);
	}
	if (kind === "knowledge") {
		return stageFromKnowledge(await runPipelineKnowledgeSandbox(MERCHANT_ID, contextualMessage));
	}
	const adminRun = await runPipelineAdminRun({
		merchant_id: MERCHANT_ID,
		message,
		history: context.history,
		previous_intent: context.previousIntent,
		current_playbook_id: context.currentPlaybookId,
		data_mode: "mock",
	});
	return withRuntimeContext(
		kind === "prompt" ? stageFromPromptResponse(adminRun) : stageFromQualityGuard(adminRun),
		context,
	);
}

function withRuntimeContext(
	result: SingleStageRuntimeResult,
	context: SingleStageRuntimeContext,
): SingleStageRuntimeResult {
	return {
		...result,
		summary: [
			{ label: "带入上文", value: `${context.history.length} 条` },
			{ label: "上一轮分类", value: intentLabel(context.previousIntent) },
			...result.summary,
		],
	};
}

function withCustomInputScope(result: SingleStageRuntimeResult): SingleStageRuntimeResult {
	return {
		...result,
		summary: [{ label: "输入口径", value: "自定义问题" }, ...result.summary],
	};
}

function withExpectedComparison(
	result: SingleStageRuntimeResult,
	kind: SingleStageRuntimeKind,
	expectedIntent: string,
	expectedPlaybook: string,
): SingleStageRuntimeResult {
	if (kind !== "intent" && kind !== "playbook") return result;
	const actualIntent = extractActualIntent(result.raw);
	const actualPlaybook = extractActualPlaybook(result.raw);
	const intentMatched = actualIntent === expectedIntent;
	const playbookMatched = actualPlaybook === expectedPlaybook;
	const matched = kind === "intent" ? intentMatched : playbookMatched;
	const comparison =
		kind === "intent"
			? intentMatched
				? "分类一致"
				: `分类不一致：实际 ${intentLabel(actualIntent)}`
			: playbookMatched
				? "方案一致"
				: `方案不一致：实际 ${playbookLabel(actualPlaybook)}`;
	return {
		...result,
		status: matched ? result.status : "partial",
		summary: [
			{ label: "预期对比", value: comparison },
			{ label: "预期分类", value: intentLabel(expectedIntent) },
			{ label: "预期方案", value: playbookLabel(expectedPlaybook) },
			...result.summary,
		],
	};
}

function extractActualIntent(raw: unknown): string | null {
	const record = asRecord(raw);
	if (!record) return null;
	if (typeof record.intent === "string") return record.intent;
	const intentRecord = asRecord(record.intent);
	if (typeof intentRecord?.intent === "string") return intentRecord.intent;
	const messageRecord = asRecord(record.messages_response);
	const messageIntent = asRecord(messageRecord?.intent);
	return typeof messageIntent?.intent === "string" ? messageIntent.intent : null;
}

function extractActualPlaybook(raw: unknown): string | null {
	const record = asRecord(raw);
	if (!record) return null;
	const playbookRecord = asRecord(record.recommended_playbook);
	if (typeof playbookRecord?.id === "string") return playbookRecord.id;
	const messageRecord = asRecord(record.messages_response);
	const messagePlaybook = asRecord(messageRecord?.recommended_playbook);
	return typeof messagePlaybook?.id === "string" ? messagePlaybook.id : null;
}

function stageFromIntent(data: PipelineIntentResponse): SingleStageRuntimeResult {
	return {
		title: "意图分类结果",
		status: "success",
		summary: [
			{ label: "实际分类", value: intentLabel(data.intent) },
			{ label: "置信度", value: formatPercent(data.confidence) },
			{ label: "识别路径", value: implementationLevelLabel(data.implementation_level) },
			{ label: "规则命中", value: intentRuleLabel(data.l2_matched_rule_id) },
			{ label: "服务阶段", value: serviceStageLabel(data.service_stage) },
		],
		raw: data,
	};
}

function implementationLevelLabel(level: string): string {
	if (level === "L2") return "L2 快路径";
	if (level === "L1") return "L1 主路径";
	if (level === "fallback") return "降级兜底";
	return level || "未返回";
}

function stageFromDispatch(data: PipelineDispatchResponse): SingleStageRuntimeResult {
	const shortCircuitSkill = data.short_circuit_skill ?? null;
	const targetLabel = shortCircuitSkill
		? skillLabel(shortCircuitSkill)
		: playbookLabel(data.recommended_playbook?.id ?? "无方案命中");
	const branchLabel = data.selected_branch ? `${data.selected_branch}` : "PB 默认";
	return {
		title: "处理方案结果",
		status: data.recommended_playbook || shortCircuitSkill ? "success" : "partial",
		summary: [
			{ label: "实际分类", value: intentLabel(data.intent.intent) },
			{ label: shortCircuitSkill ? "命中短路径" : "命中方案", value: targetLabel },
			{ label: "方案分支", value: branchLabel },
			{ label: "命中方式", value: matchModeLabel(data.match_mode) },
			{ label: "规则 ID", value: data.routing_rule_id ?? "未返回" },
			{ label: "方案名称", value: data.recommended_playbook?.name ?? "通用兜底" },
			{ label: "推荐工具", value: skillListLabel(data.recommended_skills) },
		],
		raw: data,
	};
}

function stageFromTools(
	oauth: PromiseSettledResult<unknown>,
	skill: PromiseSettledResult<unknown>,
	knowledge: PromiseSettledResult<unknown>,
): SingleStageRuntimeResult {
	const failedCount = [oauth, skill, knowledge].filter((item) => item.status === "rejected").length;
	return {
		title: "工具与动作结果",
		status: failedCount === 0 ? "success" : failedCount === 3 ? "error" : "partial",
		summary: [
			{ label: "Shopify/OAuth", value: settledText(oauth, oauthText) },
			{ label: skillLabel("SK05"), value: settledText(skill, skillText) },
			{ label: "知识辅助", value: settledText(knowledge, knowledgeText) },
			{ label: "失败项", value: String(failedCount) },
		],
		raw: {
			oauth: settledRaw(oauth),
			skill: settledRaw(skill),
			knowledge: settledRaw(knowledge),
		},
	};
}

function stageFromKnowledge(
	data: Awaited<ReturnType<typeof runPipelineKnowledgeSandbox>>,
): SingleStageRuntimeResult {
	return {
		title: "知识检索结果",
		status: data.total > 0 ? "success" : "partial",
		summary: [
			{ label: "命中数", value: String(data.total) },
			{ label: "来源", value: knowledgeSourceLabel(data.source) },
			{ label: "审计号", value: data.audit_id ?? "未返回" },
			{ label: "Top 文档", value: data.items.at(0)?.title ?? "未命中" },
		],
		raw: data,
	};
}

function stageFromPromptResponse(data: PipelineAdminRunResponse): SingleStageRuntimeResult {
	const generationEvent = data.events.find((event) => event.event_type === "generation_completed");
	const phase4 = data.messages_response.phase_trace.find(
		(entry) => entry.phase === "phase4_generate",
	);
	const snapshot = asRecord(generationEvent?.raw.prompt_snapshot);
	const evidenceRefs = Array.isArray(generationEvent?.evidence_refs)
		? generationEvent.evidence_refs.map((item) => String(item))
		: Array.isArray(phase4?.summary.evidence_refs)
			? phase4.summary.evidence_refs.map((item) => String(item))
			: [];
	return {
		title: "回复生成结果",
		status: generationEvent ? "success" : "partial",
		summary: [
			{ label: "生成事件", value: eventLabel(generationEvent?.event_type ?? "未返回生成事件") },
			{
				label: "提示词版本",
				value:
					snapshot?.prompt_version == null ? "未返回" : stringifyScalar(snapshot.prompt_version),
			},
			{ label: "证据引用", value: evidenceRefs.join(", ") || "未返回" },
			{
				label: "最终回复",
				value: data.final_reply || data.messages_response.reply_text || "未返回",
			},
		],
		raw: {
			run_id: data.run_id,
			trace_id: data.trace_id,
			generation_event: generationEvent ?? null,
			phase4: phase4 ?? null,
			messages_response: data.messages_response,
		},
	};
}

function stageFromQualityGuard(data: PipelineAdminRunResponse): SingleStageRuntimeResult {
	const qualityEvent = data.events.find((event) => event.event_type === "quality_completed");
	const phase5 = data.messages_response.phase_trace.find(
		(entry) => entry.phase === "phase5_validate",
	);
	const eventOutput = asRecord(qualityEvent?.output);
	const rawSummary = asRecord(qualityEvent?.raw.summary) ?? asRecord(phase5?.summary);
	const decision =
		(eventOutput?.decision == null ? "" : stringifyScalar(eventOutput.decision)) ||
		(rawSummary?.decision == null ? "" : stringifyScalar(rawSummary.decision)) ||
		(phase5?.status == null ? "" : stringifyScalar(phase5.status)) ||
		"未返回";
	const blockReason =
		(rawSummary?.block_reason == null ? "" : stringifyScalar(rawSummary.block_reason)) ||
		(eventOutput?.block_reason == null ? "" : stringifyScalar(eventOutput.block_reason)) ||
		"无";
	return {
		title: "质量策略结果",
		status: qualityEvent?.status === "blocked" ? "partial" : qualityEvent ? "success" : "partial",
		summary: [
			{ label: "质量事件", value: eventLabel(qualityEvent?.event_type ?? "未返回质量事件") },
			{ label: "质量判定", value: qualityDecisionLabel(decision) },
			{ label: "阻断原因", value: blockReason },
			{
				label: "最终动作",
				value: finalActionLabel(data.final_action_type || data.messages_response.final_action_type),
			},
		],
		raw: {
			run_id: data.run_id,
			trace_id: data.trace_id,
			quality_event: qualityEvent ?? null,
			phase5: phase5 ?? null,
			messages_response: data.messages_response,
		},
	};
}

function oauthText(value: unknown): string {
	const item = value as {
		message?: string;
		shopify_api_status?: string;
		shopify_domain?: string;
		checked_live?: boolean;
	};
	return `${item.message ?? "OAuth 已检查"}；${item.shopify_domain ?? "unknown shop"}；status=${item.shopify_api_status ?? "unknown"}；live=${item.checked_live ? "yes" : "no"}`;
}

function skillText(value: unknown): string {
	const item = value as {
		skill_id?: string;
		message?: string;
		executed?: boolean;
		dry_run_only?: boolean;
	};
	return `${skillLabel(item.skill_id)}；${item.message ?? "工具沙盒已检查"}；执行=${item.executed ? "是" : "否"}；保护性试跑=${item.dry_run_only ? "是" : "否"}`;
}

function knowledgeText(value: unknown): string {
	const item = value as { audit_id?: string; total?: number; source?: string };
	return `${item.audit_id ?? "no audit"}；命中 ${item.total ?? 0} 条；来源 ${knowledgeSourceLabel(item.source ?? "unknown")}`;
}

function settledText<T>(result: PromiseSettledResult<T>, formatter: (value: T) => string): string {
	if (result.status === "fulfilled") return formatter(result.value);
	return `失败：${errorMessage(result.reason)}`;
}

function settledRaw<T>(result: PromiseSettledResult<T>): unknown {
	if (result.status === "fulfilled") return result.value;
	return { error: errorMessage(result.reason) };
}

function createEvalHref({
	targetStage,
	sampleId,
}: {
	targetStage: SingleStageRuntimeTargetStage;
	sampleId: string;
}) {
	const params = new URLSearchParams({
		mock: "1",
		source: "p10_single_stage",
		sample_id: sampleId,
		target_stage: targetStage,
		candidate_mode: "manual",
	});
	return `/eval?${params.toString()}#single-stage-candidate`;
}

function isCustomRuntimeMessage(message: string, sampleMessage: string): boolean {
	return message.trim() !== sampleMessage.trim();
}

function errorMessage(error: unknown): string {
	if (isAxiosError(error)) {
		const detail = error.response?.data;
		if (typeof detail === "string") return detail;
		if (detail && typeof detail === "object" && "detail" in detail) {
			return String((detail as { detail: unknown }).detail);
		}
		return error.message;
	}
	if (error instanceof Error) return error.message;
	return String(error);
}

function formatPercent(value: number): string {
	return `${Math.round(value * 100)}%`;
}

function asRecord(value: unknown): Record<string, unknown> | null {
	if (value && typeof value === "object" && !Array.isArray(value)) {
		return value as Record<string, unknown>;
	}
	return null;
}

function stringifyScalar(value: unknown): string {
	if (Array.isArray(value)) return value.map((item) => stringifyScalar(item)).join(",");
	if (value === null || value === undefined) return "无";
	if (typeof value === "object") return JSON.stringify(value);
	return String(value);
}
